import api from "../utils/axiosInstance";
import * as C from "../constants/promoCodeConstants";
import { getApiErrorMessage } from "../utils/errorMessages";

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeApiError(error, fallback) {
  return getApiErrorMessage(error, { fallback });
}

function normalizeCode(value = "") {
  return safeText(value).toUpperCase().replace(/\s+/g, "");
}

function normalizeProductIds(values = []) {
  if (!Array.isArray(values)) return [];

  const seen = new Set();
  const ids = [];

  values.forEach((value) => {
    const id = safeText(value);

    if (!id || seen.has(id)) return;

    seen.add(id);
    ids.push(id);
  });

  return ids;
}

function normalizePayload(payload = {}) {
  return {
    code: normalizeCode(payload.code),
    name: safeText(payload.name),
    description: safeText(payload.description),
    discountType:
      safeText(payload.discountType).toLowerCase() === "fixed"
        ? "fixed"
        : "percent",
    discountValue: Number(payload.discountValue || 0),
    minimumSubtotal: Number(payload.minimumSubtotal || 0),
    maxDiscount: Number(payload.maxDiscount || 0),
    currency: safeText(payload.currency || "GBP").toUpperCase(),
    productIds: normalizeProductIds(payload.productIds),
    startsAt: safeText(payload.startsAt) || null,
    endsAt: safeText(payload.endsAt) || null,
    usageLimit: Number(payload.usageLimit || 0),
    perUserLimit: Number(payload.perUserLimit || 0),
    isActive: Boolean(payload.isActive),
  };
}

export const listPromoCodesAdmin =
  (params = {}) =>
  async (dispatch) => {
    try {
      dispatch({ type: C.PROMO_CODE_ADMIN_LIST_REQUEST });

      const { data } = await api.get("/promo-codes/admin", { params });

      dispatch({
        type: C.PROMO_CODE_ADMIN_LIST_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payload = normalizeApiError(
        error,
        "We could not load promo codes right now. Please try again later.",
      );

      dispatch({
        type: C.PROMO_CODE_ADMIN_LIST_FAIL,
        payload,
      });

      throw new Error(payload);
    }
  };

export const getPromoCodeDetails = (id) => async (dispatch) => {
  try {
    dispatch({ type: C.PROMO_CODE_DETAILS_REQUEST });

    const { data } = await api.get(
      `/promo-codes/admin/${encodeURIComponent(safeText(id))}`,
    );

    dispatch({
      type: C.PROMO_CODE_DETAILS_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = normalizeApiError(
      error,
      "We could not load this promo code right now. Please try again later.",
    );

    dispatch({
      type: C.PROMO_CODE_DETAILS_FAIL,
      payload,
    });

    throw new Error(payload);
  }
};

export const createPromoCode = (payload = {}) => async (dispatch) => {
  try {
    dispatch({ type: C.PROMO_CODE_CREATE_REQUEST });

    const { data } = await api.post(
      "/promo-codes/admin",
      normalizePayload(payload),
    );

    dispatch({
      type: C.PROMO_CODE_CREATE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(
      error,
      "We could not create this promo code right now. Please try again later.",
    );

    dispatch({
      type: C.PROMO_CODE_CREATE_FAIL,
      payload: payloadError,
    });

    throw new Error(payloadError);
  }
};

export const updatePromoCode =
  (id, payload = {}) =>
  async (dispatch) => {
    try {
      dispatch({ type: C.PROMO_CODE_UPDATE_REQUEST });

      const { data } = await api.put(
        `/promo-codes/admin/${encodeURIComponent(safeText(id))}`,
        normalizePayload(payload),
      );

      dispatch({
        type: C.PROMO_CODE_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "We could not save this promo code right now. Please try again later.",
      );

      dispatch({
        type: C.PROMO_CODE_UPDATE_FAIL,
        payload: payloadError,
      });

      throw new Error(payloadError);
    }
  };

export const deletePromoCode = (id) => async (dispatch) => {
  try {
    dispatch({ type: C.PROMO_CODE_DELETE_REQUEST });

    const { data } = await api.delete(
      `/promo-codes/admin/${encodeURIComponent(safeText(id))}`,
    );

    dispatch({
      type: C.PROMO_CODE_DELETE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = normalizeApiError(
      error,
      "We could not delete this promo code right now. Please try again later.",
    );

    dispatch({
      type: C.PROMO_CODE_DELETE_FAIL,
      payload,
    });

    throw new Error(payload);
  }
};
